import dayjs from 'dayjs';
import { View } from '@react-pdf/renderer';
import { DateUtils } from '../../../common';
import { en } from '../../theme/languages/en';
import { Span } from '../Span/Span';
import { DATE_FORMAT_SLASH } from '../../utils/invoiceTable';
import { getNetTermLabel } from '../../utils/general';
import { getCreditNoteIdName } from '../../utils';
import { Payout } from '../../../common/interfaces/payout.interface';
import { styles } from './PayoutMetaData.module';

interface PayoutMetaDataProps {
  payoutData: Payout;
  title: string;
  className?: {};
  showBillingPeriod?: boolean;
}

export const PayoutMetaData = ({ title, payoutData, className, showBillingPeriod }: PayoutMetaDataProps) => {
  const dueDate =
    payoutData?.customer &&
    dayjs(
      DateUtils.getDueDate(
        payoutData?.issueDate || new Date(),
        isNaN(+payoutData?.netTerms) ? payoutData?.netTerms : +payoutData?.netTerms,
        payoutData?.customer?.country,
      ),
    ).format(DATE_FORMAT_SLASH);

  return (
    <View style={className}>
      <View style={styles.invoiceDetails}>
        {payoutData?.poNumber && <Span style={styles.po}>{payoutData?.poNumber}</Span>}
        <Span style={styles.mainTitle} canBeMissingValue missingValue={title}>
          {title}
        </Span>

        <View style={styles.detailsContainer}>
          <View style={styles.detailsSection}>
            <Span style={styles.detailsTitle}>{en.payTo}</Span>
            <Span style={styles.text}>{payoutData?.customer?.legalName}</Span>
            <Span style={styles.text}>{payoutData?.customer?.billingAddress}</Span>
            <Span style={styles.text}>{payoutData?.customer?.city}</Span>
            <Span style={styles.text}>{payoutData?.customer?.countryState}</Span>
            <Span style={styles.text}>{payoutData?.customer?.country}</Span>
          </View>

          <View style={[styles.detailsSection, { marginLeft: 80 }]}>
            <View style={styles.textContainer}>
              <Span style={styles.detailsTitle}>{en.payoutNo}</Span>
              <Span
                style={styles.text}
                canBeMissingValue
                missingValue={payoutData?.documentNumber || payoutData?.draftNumber}
                placeholder={en.draft}
              >
                {getCreditNoteIdName(payoutData)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.payoutDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={payoutData?.issueDate} placeholder={'-'}>
                {dayjs(payoutData?.issueDate).format(DATE_FORMAT_SLASH)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.dueDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={dueDate} placeholder={'-'}>
                {dueDate}
                {!!+payoutData?.netTerms && (
                  <Span style={styles.text} canBeMissingValue missingValue={payoutData?.netTerms}>
                    {getNetTermLabel(payoutData?.netTerms)}
                  </Span>
                )}
              </Span>
            </View>

            {showBillingPeriod && (
              <View style={styles.textContainer}>
                <Span style={styles.text}>{`${en.billingPeriod}:`}</Span>
                <Span
                  style={styles.text}
                  canBeMissingValue
                  missingValue={
                    payoutData?.pricingModel?.tabs[0]?.billingStartDate ||
                    payoutData?.pricingModel?.tabs[0]?.billingEndDate
                  }
                  placeholder={'-'}
                >{`${dayjs(payoutData?.pricingModel?.tabs[0]?.billingStartDate).format(DATE_FORMAT_SLASH)} - ${dayjs(
                  payoutData?.pricingModel?.tabs[0]?.billingEndDate,
                ).format(DATE_FORMAT_SLASH)}`}</Span>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
